import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { VISIBILITY_ROLES, matchLocation } from '../config/pageVisibility';
const API_URL = process.env.REACT_APP_API_URL || '/api';
const PageVisibilityContext = createContext(undefined);
export const usePageVisibility = () => {
    const context = useContext(PageVisibilityContext);
    if (context === undefined) {
        throw new Error('usePageVisibility must be used within a PageVisibilityProvider');
    }
    return context;
};
function isVisibilityRole(role) {
    return !!role && VISIBILITY_ROLES.includes(role);
}
export const PageVisibilityProvider = ({ children }) => {
    const { user, isAuthenticated } = useAuth();
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (!isAuthenticated || !isVisibilityRole(user?.role)) {
            setSettings(null);
            return;
        }
        setLoading(true);
        axios
            .get(`${API_URL}/rbac/page-visibility`)
            .then((res) => setSettings(res.data?.data || null))
            .catch(() => setSettings(null))
            .finally(() => setLoading(false));
    }, [isAuthenticated, user?.role]);
    const canViewKey = (key) => {
        if (!isVisibilityRole(user?.role))
            return true;
        if (loading || !settings)
            return true; // fail open while loading/unavailable
        const roleSettings = settings[user.role];
        if (!roleSettings || !(key in roleSettings))
            return true;
        return roleSettings[key] !== false;
    };
    const canViewPath = (pathname) => canViewLocation(pathname, '');
    const canViewLocation = (pathname, search) => {
        const match = matchLocation(pathname, search);
        if (!match)
            return true; // pages not in the configurable list are always allowed
        if (!canViewKey(match.pageKey))
            return false;
        if (match.subPageKey && !canViewKey(match.subPageKey))
            return false;
        return true;
    };
    return (_jsx(PageVisibilityContext.Provider, { value: { canViewKey, canViewPath, canViewLocation, loading }, children: children }));
};
